import React, { useCallback, useMemo } from 'react'
import moment from 'moment'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { GridRowParams } from '@mui/x-data-grid-pro'
import { DataGrid, useDataGridProps } from '@cbgms/components/list/DataGrid'
import { useListService } from '@cbgms/components/list/use-list-service/use-list-service'
import { useDataGridActions } from '@cbgms/components/list/use-data-grid-actions'
import { Box, Stack, Typography } from '@mui/material'
import { ProductWarrantyClaimRowResponse } from '@cbgms/api'
import { DataGridHeader } from 'app-support/road-assistance/components/DataGridHeader'
import { getWarrantyClaimStatusColorCode, warrantyClasses } from '../helpers/productWarrantyClaimsColorCodes'
import LicensePlate from '@cbgms/components/form/LicensePlate'
import { useEnumTranslation } from '@cbgms/base/enums/use-enum-translation'
import ViewAction from '../components/ViewAction'
import { DataGridToolbarContainer, DataGridToolbarDefaultButtons } from '@cbgms/components/list/DataGridToolbar'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { IRootReducerState } from 'state/reducers'

const WarrantyList: React.FC = () => {
  const { t } = useTranslation()
  const { te } = useEnumTranslation()
  const match = useRouteMatch()
  const history = useHistory()
  const classes = warrantyClasses()

  // Get user ID for search history scoping
  const userID = useSelector((state: IRootReducerState) => state.employee?.details?.UUID)
  const getUserID = useCallback(() => userID || 'anonymous', [userID])
  const onEdit = React.useCallback(
    (row: ProductWarrantyClaimRowResponse) => {
      history.push(`${match.url}/edit/${row.Claim.UUID}`)
    },
    [match.url]
  )

  const rowActions = (params: GridRowParams<ProductWarrantyClaimRowResponse>) => {
    return [<ViewAction row={params.row} />]
  }

  const handleDoubleClick = (params: GridRowParams<ProductWarrantyClaimRowResponse>) => onEdit(params.row)

  const listService = useListService<ProductWarrantyClaimRowResponse>({
    application: 'support',
    module: 'support-product-warranty-claims',
    setting: 'support-product-warranty-claims/list'
  })

  const dataGridProps = useDataGridProps<ProductWarrantyClaimRowResponse>({
    key: 'support-product-warranty-claims',
    ...listService,
    getRowId: row => {
      return row.Claim.UUID
    }
  })

  const dataGridActionProps = useDataGridActions(dataGridProps.columns, dataGridProps.pinnedColumns, rowActions)

  dataGridActionProps.columns = useMemo(() => {
    return dataGridActionProps.columns.map(column => {
      if (column.field === 'Claim.Status') {
        return {
          ...column,
          renderHeader: () => <DataGridHeader field={column.field} />,
          renderCell: params => (
            <span className={classes.boldFont} style={{ color: getWarrantyClaimStatusColorCode(params.value) }}>
              {te('ProductWarrantyClaimStatus', params.value)}
            </span>
          ),
          filterable: true
        }
      } else if (column.field === 'Claim.CreatedAt') {
        return {
          ...column,
          renderHeader: () => <DataGridHeader field={column.field} />,
          renderCell: params => <span>{moment(params.value).format('DD/MM/YYYY')}</span>
        }
      } else if (column.field === 'Claim.LicensePlate') {
        return {
          ...column,
          renderHeader: () => <DataGridHeader field={column.field} />,
          renderCell: params => <LicensePlate registration={params.value} />
        }
      } else if (column.field !== 'actions') {
        return {
          ...column,
          renderHeader: () => <DataGridHeader field={column.field} />
        }
      } else {
        return {
          ...column,
          width: column.field === 'actions' ? 120 : column.width
        }
      }
    })
  }, [dataGridActionProps.columns, classes.buleFont, classes.redFont, classes.greenFont, classes.blackFont, classes.boldFont])

  return (
    <Stack>
      <Box mb={1}>
        <Typography variant='h6' component='h2'>
          {t(`productWarrantyClaim.title`)}
        </Typography>
      </Box>
      <DataGrid
        {...dataGridProps}
        {...dataGridActionProps}
        slots={{ toolbar: () => SmartAlertToolbar({ getUserID }) }}
        onRowDoubleClick={handleDoubleClick}
        disableColumnMenu
      />
    </Stack>
  )
}

function SmartAlertToolbar({ getUserID }: { getUserID: () => string }) {
  return (
    <DataGridToolbarContainer>
      <DataGridToolbarDefaultButtons
        historyButton={{
          scope: 'support-product-warranty-claims',
          getUserID
        }}
      />
    </DataGridToolbarContainer>
  )
}

export default WarrantyList
